import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { theme } from './theme';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { Subscriptions } from './pages/Subscriptions';
import { SubscriptionDetail } from './pages/SubscriptionDetail';
import { Assets } from './pages/Assets';
import { TxHistory } from './pages/TxHistory';
import { MyPage } from './pages/MyPage';
import { AdminSignups } from './pages/AdminSignups';
import { AdminRoles } from './pages/AdminRoles';
import { AdminMonitoring } from './pages/AdminMonitoring';
import { AdminKeys } from './pages/AdminKeys';
import { AdminRegistry } from './pages/AdminRegistry';
import { Explorer } from './pages/Explorer';
import { IssuerSubscriptions, IssuerStablecoin } from './pages/Issuer';
import { IssuerSubscriptionOps } from './pages/IssuerSubscriptionOps';
import { IssuerSecurities } from './pages/IssuerSecurities';

function Loading() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );
}

/** 로그인 필요 + (옵션) 역할 검사 */
function Protected({ roles, children }: { roles?: string[]; children: React.ReactNode }) {
  const { me, loading, hasRole } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  if (roles && !hasRole(...roles)) return <Navigate to="/" replace />;
  // 오류 경계는 **레이아웃 안쪽**에 둔다 — 화면 하나가 죽어도 네비게이션은 살아 있어야
  // 사용자가 다른 메뉴로 빠져나갈 수 있다.
  return (
    <Layout>
      <ErrorBoundary resetKey={loc.pathname}>{children}</ErrorBoundary>
    </Layout>
  );
}

/** 역할별 기본 랜딩 */
function Home() {
  const { me, loading, hasRole } = useAuth();
  if (loading) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  if (hasRole('admin')) return <Navigate to="/admin/signups" replace />;
  if (hasRole('issuer_st')) return <Navigate to="/issuer/subscriptions" replace />;
  if (hasRole('issuer_sc')) return <Navigate to="/issuer/stablecoin" replace />;
  return <Navigate to="/subscriptions" replace />;
}

export function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/" element={<Home />} />
            <Route path="/subscriptions" element={<Protected roles={['user']}><Subscriptions /></Protected>} />
            <Route path="/subscriptions/:id" element={<Protected roles={['user']}><SubscriptionDetail /></Protected>} />
            <Route path="/assets" element={<Protected roles={['user']}><Assets /></Protected>} />
            <Route path="/tx" element={<Protected roles={['user']}><TxHistory /></Protected>} />
            <Route path="/mypage" element={<Protected><MyPage /></Protected>} />
            <Route path="/issuer/subscriptions" element={<Protected roles={['issuer_st', 'admin']}><IssuerSubscriptions /></Protected>} />
            <Route path="/issuer/subscriptions/:id" element={<Protected roles={['issuer_st', 'admin']}><IssuerSubscriptionOps /></Protected>} />
            <Route path="/issuer/securities" element={<Protected roles={['issuer_st']}><IssuerSecurities /></Protected>} />
            <Route path="/issuer/stablecoin" element={<Protected roles={['issuer_sc']}><IssuerStablecoin /></Protected>} />
            <Route path="/admin/signups" element={<Protected roles={['admin']}><AdminSignups /></Protected>} />
            <Route path="/admin/roles" element={<Protected roles={['admin']}><AdminRoles /></Protected>} />
            <Route path="/admin/monitoring" element={<Protected roles={['admin']}><AdminMonitoring /></Protected>} />
            <Route path="/admin/keys" element={<Protected roles={['admin']}><AdminKeys /></Protected>} />
            <Route path="/admin/registry" element={<Protected roles={['admin']}><AdminRegistry /></Protected>} />
            {/* 자체 조회는 발행자도 필요하다(자기 토큰의 이동을 확인) */}
            <Route path="/explorer" element={<Protected roles={['admin', 'issuer_st', 'issuer_sc']}><Explorer /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
